import React, { ChangeEvent } from "react";
import { RouteComponentPropsHistory } from "../../types";
import { DateTime } from "luxon";

import { V2 } from "../../Api";
import { formatDateLocal, formatName } from "../../Utilities";

import Checkbox from "../Checkbox";
import withAuth from "../withAuth";
import withTitle from "../withTitle";
import { IAuthProps } from "../AuthContext";

import "../../iframe.css";

interface IEmailOutreachReviewViewState {
    isLoading: boolean | string;

    settings: any;
    template: any;
    subject: string;
    resultCount: number;

    emailConsent: boolean;
}

class EmailOutreachReviewView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IEmailOutreachReviewViewState> {
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const state: IEmailOutreachReviewViewState = {
            isLoading: false,

            settings: props.location.state?.settings || {},
            template: props.location.state?.template || {},
            subject: props.location.state?.subject || "",
            resultCount: props.location.state?.resultCount || 0,

            emailConsent: false,
        };

        if (props.location.state?.settings.excludeIds) {
            state.resultCount -= props.location.state.settings.excludeIds.length;
        }

        this.state = state;

        this.handleInputChanged = this.handleInputChanged.bind(this);
        this.beginSending = this.beginSending.bind(this);
    }

    public componentDidMount() {
    }

    public render() {
        const employeeName = formatName((this.props.authContext.user.employee as any).name);
        // const employeePhone = formatPhone((this.props.authContext.user.employee as any).phone);
        const employeeEmail = (this.props.authContext.user.employee as any).email;


        return (
            <div className="viewer-root">
                <p>Please review your filters and template selection below.</p>

                <h2>Settings</h2>
                <ul>
                    <li>Date range: <b>{formatDateLocal(this.state.settings.dateStart) || "Any"}</b> &mdash; <b>{formatDateLocal(this.state.settings.dateEnd) || "Any"}</b></li>
                    <li>Age range: <b>{this.state.settings.ageMin || "Any"}</b> &mdash; <b>{this.state.settings.ageMax || "Any"}</b></li>
                    <li>Sex: <b>{this.sexToText(this.state.settings.sex || "Any")}</b></li>
                    <li>Fair: {this.state.settings.fairId ?
                        <> <b>{this.state.settings.fair.name}</b> on <b>{formatDateLocal(this.state.settings.fair.scheduledDate)}</b></>
                        : "N/A"}
                    </li>
                    <li>
                        Answered <b>Yes</b> to:
                        <ul className="list-margin">
                            {this.state.settings.selectedYesQuestions?.length
                                ? this.state.settings.selectedYesQuestions.map((a: any) => <li key={a.id}>{a.text}</li>)
                                : <span className="text-light">None selected</span>}
                        </ul>
                    </li>
                    <li>
                        Answered <b>No</b> to:
                        <ul className="list-margin">
                            {this.state.settings.selectedNoQuestions?.length
                                ? this.state.settings.selectedNoQuestions.map((a: any) => <li key={a.id}>{a.text}</li>)
                                : <span className="text-light">None selected</span>}
                        </ul>
                    </li>
                    {this.state.settings.excludeIds.length
                        ? <li>Survey(s) excluded: <b>{this.state.settings.excludeIds.length}</b></li>
                        : null}
                    <li>Message subject: <b>{this.state.subject}</b></li>
                    <li>Message template: <b>{this.state.template.name}</b></li>
                </ul>
                <p>The above settings match {this.state.resultCount} questionnaire(s)</p>
                <p>
                    Emails will be sent using the name & email address on file for the currently logged-in account:<br />
                    <b>{employeeName} ({employeeEmail || "No email address"})</b>
                </p>
                {!employeeEmail
                    ? <div className="error-box">No email address is on file for this account! You cannot send emails from this account.</div>
                    : <>
                        <p>Once you've verified everything above looks correct, you may begin sending emails.</p>
                        <section className="status-box">
                            <Checkbox id="frm-email-consent" fancy={true} className="white" onChange={this.handleInputChanged} name="emailConsent" checked={this.state.emailConsent} />
                            <label htmlFor="frm-email-consent">Yes, the information above is correct and I understand that by clicking Start Sending below, <b>{this.state.resultCount.toLocaleString()}</b> emails will be sent.</label>
                        </section>
                        <div className="action-panel-dialog">
                            <button type="button" className="button" disabled={!this.state.emailConsent} onClick={this.beginSending}>Start Sending</button>
                        </div>
                    </>}
                &nbsp;
                {this.state.isLoading === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.isLoading === "string"
                        ? <div className="error-text">{this.state.isLoading}</div>
                        : null
                }
            </div>
        );
    }

    private handleInputChanged(e: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement> | ChangeEvent<HTMLSelectElement>) {
        const name = e.currentTarget.name;
        const value = e.currentTarget.type === "checkbox" ? (e.currentTarget as HTMLInputElement).checked : e.currentTarget.value;

        this.setState({
            [name]: value
        } as any);
    }

    private sexToText(sex: string) {
        switch (sex) {
            case "F":
            case "f":
                return "Female";
            case "M":
            case "m":
                return "Male";
            default:
                return sex;
        }
    }

    private async beginSending() {
        try {
            const settings = Object.assign({}, this.state.settings, {
                dateStart: this.state.settings.dateStart ? DateTime.fromISO(this.state.settings.dateStart).startOf("day").toISO() : undefined,
                dateEnd: this.state.settings.dateEnd ? DateTime.fromISO(this.state.settings.dateEnd).endOf("day").toISO() : undefined,

                questionsYes: this.state.settings.selectedYesQuestions.map((a: any) => a.id),
                questionsNo: this.state.settings.selectedNoQuestions.map((a: any) => a.id),
            });

            delete settings.selectedYesQuestions;
            delete settings.selectedNoQuestions;

            const data = {
                settings: settings,
                templateId: this.state.template.id,
                messageSubject: this.state.subject,
            };

            this.setState({
                isLoading: true
            });

            await V2.post("survey/outreach/email/batch", data);

            this.props.history.go(-3);
        }
        catch (e) {
            this.setState({
                isLoading: e.message || "Could not start sending"
            });
        }
    }
}

export default withTitle("Email Outreach Review", withAuth(EmailOutreachReviewView));